import React, { useEffect, useMemo, useState } from "react";
import { HiRefresh, HiOutlineDesktopComputer, HiOutlineClock, HiOutlineChevronRight, HiOutlineSearch, HiOutlineX } from "react-icons/hi";
import { useHistory } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import API from "../../api";
import { useSocket } from "../../contexts/SocketContext";

const formatLastSeen = (lastSeenAt) => {
  if (!lastSeenAt) return "Jamais vu";

  const diffMins = Math.floor((Date.now() - new Date(lastSeenAt).getTime()) / 60000);
  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Vu il y a ${diffMins} min`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `Vu il y a ${diffHours} h`;

  const diffDays = Math.floor(diffHours / 24);
  return `Vu il y a ${diffDays} j`;
};

const Machine = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { socket } = useSocket();
  const [search, setSearch] = useState("");

  const {
    data: machines = [],
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["machines"],
    queryFn: async () => {
      const response = await API.get("/machine");
      return response.data;
    },
  });

  useEffect(() => {
    if (!socket) return;

    const onPresence = (presence) => {
      const current = queryClient.getQueryData(["machines"]);
      if (!current) return;

      const exists = current.some((machine) => machine.indexName === presence.indexName && machine.botId === presence.botId);
      if (!exists) return queryClient.invalidateQueries({ queryKey: ["machines"] });

      queryClient.setQueryData(["machines"], (prev) =>
        prev.map((machine) =>
          machine.indexName === presence.indexName && machine.botId === presence.botId
            ? { ...machine, online: presence.online, lastSeenAt: presence.lastSeenAt ?? machine.lastSeenAt }
            : machine,
        ),
      );
    };

    socket.on("machine:presence", onPresence);
    return () => socket.off("machine:presence", onPresence);
  }, [socket, queryClient]);

  const filteredMachines = useMemo(() => {
    if (!search) return machines;
    const query = search.toLowerCase();
    return machines.filter((machine) => `${machine.botId} ${machine.streamName} ${machine.indexName}`.toLowerCase().includes(query));
  }, [machines, search]);

  const onlineCount = useMemo(() => machines.filter((machine) => machine.online).length, [machines]);

  return (
    <div className="min-h-[calc(100vh-60px)] bg-gray-50 p-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-gray-900">Machines</h1>
            <span className="text-sm text-gray-500">
              {onlineCount} en ligne · {machines.length} au total
            </span>
          </div>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <HiRefresh className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin [animation-direction:reverse]" : ""}`} />
            Actualiser
          </button>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-100 p-3">
            <div className="relative flex-1">
              <HiOutlineSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                autoFocus
                placeholder="Rechercher une machine..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-1.5 pl-9 pr-8 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
              {search && (
                <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <HiOutlineX className="h-4 w-4" />
                </button>
              )}
            </div>
            <span className="text-xs text-gray-500">{filteredMachines.length.toLocaleString()} résultats</span>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-primary-500" />
            </div>
          ) : filteredMachines.length === 0 ? (
            <div className="py-16 text-center text-gray-400">{machines.length === 0 ? "Aucune machine connue" : "Aucune machine trouvée"}</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredMachines.map((machine) => (
                <div
                  key={machine._id}
                  onClick={() =>
                    machine.kind !== "agent" && history.push(`/machine/${encodeURIComponent(machine.streamName)}/logs?logGroupName=${encodeURIComponent(machine.logGroupName)}`)
                  }
                  className={`flex items-center gap-3 px-3 py-3 text-sm transition-colors ${machine.kind === "agent" ? "" : "cursor-pointer hover:bg-gray-50"}`}
                >
                  <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${machine.online ? "bg-emerald-50" : "bg-gray-100"}`}>
                    <HiOutlineDesktopComputer className={`h-4 w-4 ${machine.online ? "text-emerald-600" : "text-gray-400"}`} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="truncate font-medium text-gray-900">{machine.botId}</h3>
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                        <span className={`h-1.5 w-1.5 rounded-full ${machine.online ? "bg-emerald-500" : "bg-gray-300"}`} />
                        {machine.online ? "En ligne" : "Hors ligne"}
                      </span>
                      {machine.kind === "agent" && <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">Agent de synchronisation</span>}
                    </div>
                    <p className="truncate text-xs text-gray-500">{machine.indexName}</p>
                  </div>

                  {!machine.online && (
                    <div className="flex flex-shrink-0 items-center text-xs text-gray-400">
                      <HiOutlineClock className="mr-1 h-3.5 w-3.5" />
                      {formatLastSeen(machine.lastSeenAt)}
                    </div>
                  )}

                  {machine.kind !== "agent" && <HiOutlineChevronRight className="h-4 w-4 flex-shrink-0 text-gray-300" />}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Machine;
